/**
 * useItemUpdate Hook
 * 
 * React hook for detecting when an item has been updated by another user.
 * Provides:
 * - Notification when item is updated/created by others
 * - User information about who made the update
 * - Callback when update is detected
 * - Clear pending update flag
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { itemUpdateStore, ItemUpdate } from '@/lib/stores/itemUpdateStore';
import { getCurrentUser } from '@/lib/stores/unifiedUserStore';

export type ItemType = 'object' | 'dialog' | 'document' | 'template' | 'flow';

export interface UseItemUpdateOptions {
  /** Item ID to watch for updates */
  itemId: string | undefined;
  /** Item type */
  itemType: ItemType;
  /** Game ID */
  gameId: string | undefined;
  /** Whether to actively listen for updates */
  enabled?: boolean;
  /** Called when item is updated by another user */
  onItemUpdated?: (updatedBy: string, action: 'created' | 'updated') => void;
}

export interface UseItemUpdateReturn {
  /** Whether item has pending updates from another user */
  hasPendingUpdate: boolean;
  /** Who updated the item */
  updatedBy: string | null;
  /** Name of who updated */
  updatedByName: string | null;
  /** Avatar of who updated */
  updatedByAvatar: string | null;
  /** When it was updated */
  updatedAt: string | null;
  /** The action that was performed */
  action: 'created' | 'updated' | null;
  /** Clear the pending update flag */
  clearPendingUpdate: () => void;
}

export function useItemUpdate({
  itemId,
  itemType,
  gameId,
  enabled = true,
  onItemUpdated,
}: UseItemUpdateOptions): UseItemUpdateReturn {
  const [pendingUpdate, setPendingUpdate] = useState<ItemUpdate | null>(null);

  // Refs to track current state for callbacks
  const itemIdRef = useRef(itemId);
  const itemTypeRef = useRef(itemType);
  const gameIdRef = useRef(gameId);
  const enabledRef = useRef(enabled);
  const onItemUpdatedRef = useRef(onItemUpdated);

  // Keep refs up to date
  useEffect(() => {
    itemIdRef.current = itemId;
    itemTypeRef.current = itemType;
    gameIdRef.current = gameId;
    enabledRef.current = enabled;
    onItemUpdatedRef.current = onItemUpdated;
  }, [itemId, itemType, gameId, enabled, onItemUpdated]);

  /**
   * Initialize store
   */
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!gameId || !currentUser || !enabled) return;

    // Initialize the store
    itemUpdateStore.initialize(gameId, currentUser.id);
  }, [gameId, enabled]);

  /**
   * Subscribe to updates for this item
   */
  useEffect(() => {
    if (!itemId || !enabled) return;


    const unsubscribe = itemUpdateStore.subscribe(itemId, itemType, (update) => {
      
      // Store the update
      setPendingUpdate(update);

      // Call the callback
      const updatedByName = update.profiles?.full_name || 
                           update.profiles?.username || 
                           update.profiles?.email || 
                           'Unknown User';
      
      onItemUpdatedRef.current?.(updatedByName, update.action);
    });

    return () => {
      unsubscribe();
    };
  }, [itemId, itemType, enabled]);

  /**
   * Clear pending update
   */
  const clearPendingUpdate = useCallback(() => {
    setPendingUpdate(null);
  }, [itemId, itemType]);

  // Derive state from pending update
  const hasPendingUpdate = !!pendingUpdate;
  const updatedBy = pendingUpdate?.updated_by || null;
  const updatedByName = pendingUpdate?.profiles?.full_name ||
                       pendingUpdate?.profiles?.username ||
                       pendingUpdate?.profiles?.email ||
                       null;
  const updatedByAvatar = pendingUpdate?.profiles?.avatar_url || null;
  const updatedAt = pendingUpdate?.updated_at || null;
  const action = pendingUpdate?.action || null;

  return {
    hasPendingUpdate,
    updatedBy,
    updatedByName,
    updatedByAvatar,
    updatedAt,
    action,
    clearPendingUpdate,
  };
}

export default useItemUpdate;
